import { useState } from "react";
import { THEME_LIST as LISTA_TEMAS, themeInfo as infoTema } from "@historia/shared/themes";
import { GAME_LABELS as ETIQUETAS_JOGOS, VALID_GAMES as JOGOS } from "@historia/shared/validation";
import type {
  ScoreRow as LinhaPlacar,
  GameId as IdJogo,
  GameTheme as TemaJogo,
} from "@historia/shared/types";

/** Short, friendly name from an email (the part before @, capitalized). */
function nome(email: string): string {
  const base = email.split("@")[0] ?? email;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

const ICONE_JOGO: Record<IdJogo, string> = {
  emoji: "😶‍🌫️",
  memoria: "🃏",
  shikaku: "🧩",
  sudoku: "🔢",
  pinpoint: "🎯",
  tango: "🌗",
  queens: "👑",
  wend: "🔤",
  crossclimb: "🪜",
  zip: "🧵",
};

/**
 * O placar global, com filtro por jogo — e, dentro do jogo dos emojis, por tema
 * (IDEA-025, alargado em IDEA-031/032).
 *
 * "Tudo" é mesmo tudo: os pontos dos emojis mais os das rondas cronometradas.
 *
 * Os temas são **do jogo dos emojis**, não do placar, por isso só aparecem
 * depois de se escolher esse jogo. Tê-los sempre à vista punha "Filmes" e
 * "Países" ao lado de "Shikaku" como se fossem a mesma espécie de coisa, e
 * fazia parecer que filtravam o total geral — que nunca filtraram: um tema não
 * tem nada que dizer sobre uma ronda de memória.
 *
 * Trocar de jogo limpa o tema, senão voltar aos emojis reabria numa categoria
 * escolhida há dois cliques atrás.
 *
 * As contas são todas do servidor; aqui só se escolhe a fatia a mostrar.
 */
export function Placar({
  global,
  porJogo,
  porTema,
}: {
  global: LinhaPlacar[];
  porJogo: Record<string, LinhaPlacar[]>;
  porTema: Record<string, LinhaPlacar[]>;
}) {
  const [jogo, setJogo] = useState<"tudo" | IdJogo>("tudo");
  const [tema, setTema] = useState<TemaJogo | null>(null);

  function escolherJogo(proximo: "tudo" | IdJogo) {
    setJogo(proximo);
    setTema(null);
  }

  const linhas =
    jogo === "tudo" ? global : tema ? (porTema[tema] ?? []) : (porJogo[jogo] ?? []);

  // Um tema conta PALAVRAS acertadas, não pontos: desde a v3 os pontos são da
  // ronda inteira, e uma ronda atravessa três temas — não há maneira honesta de
  // repartir 5 pontos por eles. Contar as palavras continua a responder à
  // pergunta que os temas fazem ("quem é melhor em Filmes?").
  const unidade: "pontos" | "palavras" = tema ? "palavras" : "pontos";

  const vazio =
    jogo === "tudo"
      ? "Ainda não há pontos. Joguem uma ronda para abrir o placar. 💛"
      : tema
        ? `Ainda ninguém acertou nenhuma palavra em ${infoTema(tema).label}.`
        : `Ainda não há pontos em ${ETIQUETAS_JOGOS[jogo]}.`;

  return (
    <section className="rounded-2xl border border-ouro/20 bg-creme-50 p-5">
      <h2 className="mb-3 font-titulo text-xl text-tinta-800">Placar</h2>

      {/* Filtro por jogo — carrossel horizontal (bom no telemóvel). */}
      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Pilula ativo={jogo === "tudo"} aoClicar={() => escolherJogo("tudo")}>
          Tudo
        </Pilula>
        {JOGOS.map((id) => (
          <Pilula key={id} ativo={jogo === id} aoClicar={() => escolherJogo(id)}>
            {ICONE_JOGO[id]} {ETIQUETAS_JOGOS[id]}
          </Pilula>
        ))}
      </div>

      {/* Temas — só dentro do jogo dos emojis, que é a quem pertencem. */}
      {jogo === "emoji" && (
        <div className="mt-2 flex gap-2 overflow-x-auto border-t border-ouro/10 pt-2 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Pilula ativo={tema === null} aoClicar={() => setTema(null)}>
            Todos os temas
          </Pilula>
          {LISTA_TEMAS.map((t) => (
            <Pilula key={t.key} ativo={tema === t.key} aoClicar={() => setTema(t.key)}>
              {t.icon} {t.label}
            </Pilula>
          ))}
        </div>
      )}

      {jogo === "emoji" && tema && (
        <p className="mt-2 text-xs text-tinta-500">
          Por tema conta-se as palavras acertadas — os pontos são da ronda inteira.
        </p>
      )}

      {linhas.length === 0 ? (
        <p className="mt-4 rounded-xl bg-creme px-4 py-6 text-center text-sm text-tinta-600">
          {vazio}
        </p>
      ) : (
        <ol className="mt-4 flex flex-col gap-2">
          {linhas.map((l, i) => (
            <li
              key={l.email}
              className="flex items-center justify-between rounded-xl bg-creme px-4 py-3"
            >
              <span className="flex items-center gap-2">
                <span className="text-lg" aria-hidden>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : "•"}
                </span>
                <span className="font-semibold text-tinta-800">{nome(l.email)}</span>
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-lg font-bold text-ouro-600">{l.total}</span>
                <span className="text-xs text-tinta-500">
                  {unidade === "palavras"
                    ? l.total === 1
                      ? "palavra"
                      : "palavras"
                    : l.total === 1
                      ? "ponto"
                      : "pontos"}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Pilula({
  ativo,
  aoClicar,
  children,
}: {
  ativo: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
        ativo
          ? "bg-ouro/15 text-ouro-600"
          : "text-tinta-700 hover:bg-creme-200/70 hover:text-ouro-600"
      }`}
    >
      {children}
    </button>
  );
}
